
import React, { useState, useRef } from 'react';
import { Icons } from '../constants';
import { Website, Page, Section } from '../types';
import { optimizeStylesWithAI } from '../services/gemini';

interface WebsiteCreatorProps {
  onCancel: () => void;
  onGenerate: (site: Website) => void;
  isGenerating: boolean;
}

export const WebsiteCreator: React.FC<WebsiteCreatorProps> = ({ onCancel, onGenerate, isGenerating: externalLoading }) => {
  const [step, setStep] = useState(0); 
  const [localLoading, setLocalLoading] = useState(false);
  const htmlRef = useRef<HTMLInputElement>(null);

  const [config, setConfig] = useState({
    name: '',
    htmlFiles: [] as Array<{ name: string; content: string }>,
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
  });

  const handleHtmlUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const fileData: Array<{ name: string; content: string }> = [];
    for (let i = 0; i < files.length; i++) {
      const text = await files[i].text();
      fileData.push({ name: files[i].name, content: text });
    }
    setConfig(prev => ({ ...prev, htmlFiles: [...prev.htmlFiles, ...fileData] }));
    setStep(1);
  };

  const processImport = async () => {
    setLocalLoading(true);
    try {
      const parser = new DOMParser();
      let combinedCss = "";
      const pages: Page[] = [];

      for (const file of config.htmlFiles) {
        const doc = parser.parseFromString(file.content, 'text/html');
        
        // Extract Styles
        const styles = Array.from(doc.querySelectorAll('style, link[rel="stylesheet"]'))
          .map(el => {
            if (el.tagName === 'STYLE') return el.innerHTML;
            return `/* External sheet ref: ${el.getAttribute('href')} */`;
          })
          .join('\n');
        combinedCss += styles + "\n";

        // Extract Body Content
        // We partition the body by major tags if possible, otherwise one big block
        const bodyContent = doc.body.innerHTML;
        
        const sections: Section[] = [{
          id: `sec-${Math.random().toString(36).substr(2, 5)}`,
          type: 'raw',
          content: {
            html: bodyContent,
            title: 'Main Content'
          }
        }];

        pages.push({
          name: file.name.replace('.html', '').charAt(0).toUpperCase() + file.name.replace('.html', '').slice(1),
          path: file.name === 'index.html' ? '/' : `/${file.name}`,
          sections
        });
      }

      // Optional AI optimization for the CSS (non-blocking)
      const optimizedCss = await optimizeStylesWithAI(combinedCss, config.name);

      const newSite: Website = {
        id: `site-${Math.random().toString(36).substr(2, 9)}`,
        tenantId: 'tenant-1',
        name: config.name,
        domain: `${config.name.toLowerCase().replace(/\s+/g, '-')}.com`,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        customCss: optimizedCss,
        pages: pages,
        marketing: {
          promoBars: [],
          popups: [],
          games: []
        }
      };

      onGenerate(newSite);
    } catch (err) {
      alert("Error processing HTML: " + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLocalLoading(false);
    }
  };

  const removeFile = (index: number) => {
    setConfig(prev => ({
      ...prev,
      htmlFiles: prev.htmlFiles.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl rounded-[4rem] shadow-2xl flex flex-col h-[85vh] overflow-hidden border border-white/20">
        <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
          <div className="flex gap-8">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs transition-all ${step === 0 ? 'bg-indigo-600 text-white' : 'bg-emerald-500 text-white'}`}>{step > 0 ? '✓' : '1'}</div>
              <span className={`text-[10px] font-black uppercase tracking-widest ${step === 0 ? 'text-indigo-600' : 'text-slate-400'}`}>Name</span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs transition-all ${step === 1 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'}`}>2</div>
              <span className={`text-[10px] font-black uppercase tracking-widest ${step === 1 ? 'text-indigo-600' : 'text-slate-400'}`}>Upload</span>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded-full transition-all">
            <Icons.Trash className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-12 custom-scrollbar">
          {step === 0 ? (
            <div className="space-y-12 animate-in fade-in max-w-2xl mx-auto py-12">
               <div className="text-center space-y-4">
                <h3 className="text-4xl font-black text-slate-900 tracking-tight">Project Identity</h3>
                <p className="text-slate-500 font-medium">How should we label this website in your dashboard?</p>
              </div>
              <input 
                autoFocus
                className="w-full bg-slate-50 border-2 border-slate-100 rounded-3xl px-8 py-6 outline-none focus:border-indigo-500 text-xl font-bold transition-all" 
                placeholder="e.g. My Awesome Site" 
                value={config.name} 
                onChange={e => setConfig({...config, name: e.target.value})}
              />
            </div>
          ) : (
            <div className="space-y-8 animate-in fade-in max-w-3xl mx-auto py-8">
               <div className="text-center">
                <h3 className="text-3xl font-black text-slate-900">Add HTML Source</h3>
                <p className="text-slate-500 font-medium mt-2">Every file you upload becomes an editable page.</p>
              </div>

              <div 
                onClick={() => htmlRef.current?.click()}
                className="w-full border-4 border-dashed border-slate-100 rounded-[3rem] py-16 flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-all text-slate-300 hover:text-indigo-600 group"
              >
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
                  <Icons.Plus className="w-8 h-8" />
                </div>
                <div className="text-center">
                  <span className="font-black uppercase tracking-widest text-xs block">Browse Files</span>
                </div>
              </div>
              <input type="file" ref={htmlRef} multiple accept=".html" className="hidden" onChange={handleHtmlUpload} />
              
              <div className="grid grid-cols-1 gap-3">
                {config.htmlFiles.map((file, i) => (
                  <div key={i} className="bg-white border border-slate-100 p-4 rounded-2xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 font-bold text-xs uppercase">HTML</div>
                      <div>
                        <p className="text-sm font-black text-slate-800">{file.name}</p>
                        <p className="text-[10px] text-slate-400">Ready for editing</p>
                      </div>
                    </div>
                    <button onClick={() => removeFile(i)} className="p-2 hover:bg-rose-50 text-slate-300 hover:text-rose-500 rounded-lg">
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-8 bg-slate-50 border-t border-slate-50 flex justify-between items-center">
          <button 
            disabled={step === 0} 
            onClick={() => setStep(0)} 
            className="px-8 py-3 rounded-2xl font-bold text-slate-400 hover:text-slate-900 disabled:opacity-0"
          >
            Go Back
          </button>
          
          <button 
            disabled={(!config.name && step === 0) || (config.htmlFiles.length === 0 && step === 1) || localLoading} 
            onClick={() => step === 0 ? setStep(1) : processImport()} 
            className="bg-indigo-600 text-white px-10 py-4 rounded-[2rem] font-black text-lg hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all disabled:opacity-30 flex items-center gap-3"
          >
            {localLoading ? (
              <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Processing...</>
            ) : step === 0 ? 'Next Step' : 'Import Project'}
          </button>
        </div>
      </div>
    </div>
  );
};
